/**
 * @module public/js/focusmode/researchReports.js
 * @summary Render and control seller-selected Focus Mode research report views.
 *
 * The current accordion experience remains the default. Sellers can instead
 * choose floating report panels that keep either one report or both reports
 * open while they work on the active task.
 */

export const FOCUS_MODE_RESEARCH_REPORT_DISPLAY_ACCORDION = 'accordion';
export const FOCUS_MODE_RESEARCH_REPORT_DISPLAY_SINGLE_MODAL = 'single-modal';
export const FOCUS_MODE_RESEARCH_REPORT_DISPLAY_MULTI_MODAL = 'multi-modal';
export const DEFAULT_FOCUS_MODE_RESEARCH_REPORT_DISPLAY =
  FOCUS_MODE_RESEARCH_REPORT_DISPLAY_ACCORDION;

const FOCUS_MODE_RESEARCH_REPORT_DISPLAY_VALUES = new Set([
  FOCUS_MODE_RESEARCH_REPORT_DISPLAY_ACCORDION,
  FOCUS_MODE_RESEARCH_REPORT_DISPLAY_SINGLE_MODAL,
  FOCUS_MODE_RESEARCH_REPORT_DISPLAY_MULTI_MODAL,
]);

/**
 * Normalize a stored research report display preference.
 *
 * @param {unknown} value - Raw preference value from Firestore or the profile UI.
 * @returns {'accordion'|'single-modal'|'multi-modal'} Supported presentation mode.
 */
export function normalizeFocusModeResearchReportDisplay(value) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return FOCUS_MODE_RESEARCH_REPORT_DISPLAY_VALUES.has(normalized)
    ? normalized
    : DEFAULT_FOCUS_MODE_RESEARCH_REPORT_DISPLAY;
}

/**
 * Escape text for safe interpolation into report labels and attributes.
 *
 * @param {unknown} value - Raw label or tooltip value.
 * @returns {string} HTML-safe text.
 */
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build one embedded accordion for the current research report.
 *
 * @param {{ kind: 'contact'|'company', label: string, tooltip: string, html: string, contentClassName?: string }} report - Report rendering context.
 * @returns {string} Accordion markup.
 */
function buildResearchAccordion(report) {
  const contentClassName = String(report.contentClassName || '').trim();
  return /* html */ `
    <details class="ai-accordion" data-research-report="${report.kind}">
      <summary data-focus-tooltip="${escapeHtml(report.tooltip)}">About ${escapeHtml(report.label)}</summary>
      <div class="ai-accordion__content ${contentClassName} ${report.kind}-report">${report.html}</div>
    </details>`;
}

/**
 * Build one floating, non-blocking research report panel.
 *
 * @param {{ kind: 'contact'|'company', label: string, tooltip: string, html: string, contentClassName?: string }} report - Report rendering context.
 * @param {{ kind: 'contact'|'company', label: string }} otherReport - The companion report.
 * @param {'single-modal'|'multi-modal'} display - Floating panel mode.
 * @returns {{ trigger: string, panel: string }} Trigger and panel markup fragments.
 */
function buildResearchPanel(report, otherReport, display) {
  const titleId = `focusResearchReport${
    report.kind === 'contact' ? 'Contact' : 'Company'
  }Title`;
  const panelId = `focusResearchReport${
    report.kind === 'contact' ? 'Contact' : 'Company'
  }Modal`;
  const contentClassName = String(report.contentClassName || '').trim();
  const allowsMultiple =
    display === FOCUS_MODE_RESEARCH_REPORT_DISPLAY_MULTI_MODAL;
  const otherReportAction = allowsMultiple
    ? /* html */ `
            <button
              type="button"
              class="btn-pill btn--teal btn--info-sm btn--primary-action"
              data-action="open-other-research-report"
              data-report-kind="${otherReport.kind}"
              data-open-label="Open ${escapeHtml(otherReport.kind)} report"
              data-opened-label="${escapeHtml(otherReport.kind)} report open"
            >
              Open ${escapeHtml(otherReport.kind)} report
            </button>`
    : '';
  return {
    trigger: /* html */ `
      <button
        type="button"
        class="focus-task-chip focus-task-chip--action focus-research-report-trigger"
        data-action="open-research-report"
        data-report-kind="${report.kind}"
        data-focus-tooltip="${escapeHtml(report.tooltip)}"
        data-focus-tooltip-placement="top"
        aria-controls="${panelId}"
        aria-expanded="false"
      >
        About ${escapeHtml(report.label)}
      </button>`,
    panel: /* html */ `
      <section
        id="${panelId}"
        class="focus-research-report-modal focus-research-report-modal--${report.kind} hidden"
        data-research-report-panel="${report.kind}"
        role="dialog"
        aria-modal="false"
        aria-hidden="true"
        aria-labelledby="${titleId}"
        tabindex="-1"
      >
        <header
          class="focus-research-report-modal__header focus-modal__header drag-handle"
          data-research-report-drag-handle
        >
          <div class="focus-modal__title-group">
            <p class="focus-research-report-modal__eyebrow">AI research</p>
            <h3 id="${titleId}" class="focus-research-report-modal__title">About ${escapeHtml(report.label)}</h3>
          </div>
        </header>
        <div class="focus-research-report-modal__body ${contentClassName} ${report.kind}-report">${report.html}</div>
        <footer class="focus-research-report-modal__footer focus-modal__footer">
          <div class="focus-modal__footer-actions" data-actions="${
            allowsMultiple ? '2' : '1'
          }">
            <button
              type="button"
              class="btn-pill btn--grey btn--info-sm btn--secondary-action"
              data-action="close-research-report"
              data-report-kind="${report.kind}"
            >
              Close
            </button>
            ${otherReportAction}
          </div>
        </footer>
      </section>`,
  };
}

/**
 * Build the two Focus Mode research reports for the selected presentation.
 *
 * Report HTML is expected to be sanitized/formatted by the existing AI report
 * formatter before it reaches this function.
 *
 * @param {object} [options={}] - Research rendering options.
 * @param {unknown} [options.display] - Stored presentation preference.
 * @param {string} [options.contactLabel='Contact'] - Contact display label.
 * @param {string} [options.companyLabel='Company'] - Company display label.
 * @param {string} [options.contactTooltip=''] - Contact trigger tooltip.
 * @param {string} [options.companyTooltip=''] - Company trigger tooltip.
 * @param {string} [options.contactHtml=''] - Formatted contact report HTML.
 * @param {string} [options.companyHtml=''] - Formatted company report HTML.
 * @param {string} [options.contactContentClassName=''] - Extra contact body classes.
 * @returns {string} Accordion or floating-panel markup.
 */
export function buildFocusModeResearchReportsMarkup(options = {}) {
  const display = normalizeFocusModeResearchReportDisplay(options.display);
  const reports = [
    {
      kind: 'contact',
      label: options.contactLabel || 'Contact',
      tooltip: options.contactTooltip || '',
      html: options.contactHtml || '',
      contentClassName: options.contactContentClassName || '',
    },
    {
      kind: 'company',
      label: options.companyLabel || 'Company',
      tooltip: options.companyTooltip || '',
      html: options.companyHtml || '',
      contentClassName: '',
    },
  ];

  if (display === FOCUS_MODE_RESEARCH_REPORT_DISPLAY_ACCORDION) {
    return reports.map(buildResearchAccordion).join('');
  }

  const panels = reports.map((report, index) =>
    buildResearchPanel(report, reports[index === 0 ? 1 : 0], display)
  );
  return /* html */ `
    <div class="focus-research-report-actions" aria-label="AI research reports">
      ${panels.map(({ trigger }) => trigger).join('')}
    </div>
    <div
      class="focus-research-report-modal-layer"
      data-research-report-layer
      data-presentation="${display}"
    >
      ${panels.map(({ panel }) => panel).join('')}
    </div>`;
}

/**
 * Bind open, close, focus, and Escape behavior to rendered research panels.
 *
 * @param {HTMLElement|null|undefined} root - Active Focus Mode task root.
 * @param {unknown} displayValue - Stored presentation preference.
 * @returns {{ open: (kind: 'contact'|'company') => void, close: (kind: 'contact'|'company', options?: { restoreFocus?: boolean }) => void }} Panel controls.
 */
export function bindFocusModeResearchReportPanels(root, displayValue) {
  const display = normalizeFocusModeResearchReportDisplay(displayValue);
  const layer = root?.querySelector?.('[data-research-report-layer]') || null;
  const noop = () => undefined;
  if (!layer || display === FOCUS_MODE_RESEARCH_REPORT_DISPLAY_ACCORDION) {
    return { open: noop, close: noop };
  }

  const panels = new Map(
    Array.from(layer.querySelectorAll('[data-research-report-panel]')).map(
      (panel) => [panel.dataset.researchReportPanel, panel]
    )
  );
  const triggers = new Map(
    Array.from(
      root.querySelectorAll('[data-action="open-research-report"]')
    ).map((trigger) => [trigger.dataset.reportKind, trigger])
  );
  const openOrder = [];
  let panelStackIndex = 0;

  /**
   * Bring the active floating report in front of its companion panel.
   *
   * @param {HTMLElement} panel - Research report panel.
   * @returns {void}
   */
  const bringToFront = (panel) => {
    panelStackIndex += 1;
    panel.style.zIndex = String(panelStackIndex);
  };

  /**
   * Enable the same desktop header-drag interaction as the call-script panel.
   *
   * @param {HTMLElement} panel - Research report panel.
   * @returns {void}
   */
  const enablePanelDragging = (panel) => {
    const handle = panel.querySelector('[data-research-report-drag-handle]');
    if (!handle) return;

    let dragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;

    /**
     * Finish the active drag and remove document-level listeners.
     *
     * @returns {void}
     */
    const handleDragEnd = () => {
      dragging = false;
      panel.classList.remove('is-dragging');
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };

    /**
     * Move this report panel with its captured header offset.
     *
     * @param {MouseEvent} event - Active document mouse move.
     * @returns {void}
     */
    function handleDragMove(event) {
      if (!dragging) return;
      panel.style.right = 'auto';
      panel.style.bottom = 'auto';
      panel.style.left = `${Math.round(event.clientX - dragOffsetX)}px`;
      panel.style.top = `${Math.round(event.clientY - dragOffsetY)}px`;
    }

    /**
     * Start a primary-button desktop drag from the report header.
     *
     * @param {MouseEvent} event - Header mouse down event.
     * @returns {void}
     */
    const handleDragStart = (event) => {
      if (window.innerWidth <= 900 || event.button !== 0) return;
      if (event.target?.closest?.('button, a, input, select, textarea')) {
        return;
      }
      const rect = panel.getBoundingClientRect();
      dragging = true;
      dragOffsetX = event.clientX - rect.left;
      dragOffsetY = event.clientY - rect.top;
      bringToFront(panel);
      panel.classList.add('is-dragging');
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      event.preventDefault();
    };

    handle.addEventListener('mousedown', handleDragStart);
  };

  panels.forEach(enablePanelDragging);

  /**
   * Synchronize layout and trigger state after panel visibility changes.
   *
   * @returns {void}
   */
  const syncState = () => {
    const openKinds = Array.from(panels.entries())
      .filter(([, panel]) => !panel.classList.contains('hidden'))
      .map(([kind]) => kind);
    layer.classList.toggle('has-multiple-open', openKinds.length > 1);
    triggers.forEach((trigger, kind) => {
      const isOpen = openKinds.includes(kind);
      trigger.setAttribute('aria-expanded', String(isOpen));
      trigger.classList.toggle('is-active', isOpen);
    });
    layer
      .querySelectorAll('[data-action="open-other-research-report"]')
      .forEach((button) => {
        const isOpen = openKinds.includes(button.dataset.reportKind);
        button.disabled = isOpen;
        button.textContent = isOpen
          ? button.dataset.openedLabel
          : button.dataset.openLabel;
      });
  };

  /**
   * Close a visible research panel and optionally restore trigger focus.
   *
   * @param {'contact'|'company'} kind - Report panel key.
   * @param {{ restoreFocus?: boolean }} [options={}] - Focus restoration options.
   * @returns {void}
   */
  const close = (kind, { restoreFocus = true } = {}) => {
    const panel = panels.get(kind);
    if (!panel || panel.classList.contains('hidden')) return;
    panel.classList.add('hidden');
    panel.setAttribute('aria-hidden', 'true');
    const openIndex = openOrder.indexOf(kind);
    if (openIndex >= 0) openOrder.splice(openIndex, 1);
    syncState();
    if (restoreFocus) triggers.get(kind)?.focus?.();
  };

  /**
   * Open a research panel, enforcing the seller's single/multiple choice.
   *
   * @param {'contact'|'company'} kind - Report panel key.
   * @returns {void}
   */
  const open = (kind) => {
    const panel = panels.get(kind);
    if (!panel) return;
    if (display === FOCUS_MODE_RESEARCH_REPORT_DISPLAY_SINGLE_MODAL) {
      panels.forEach((candidate, candidateKind) => {
        if (candidateKind !== kind && !candidate.classList.contains('hidden')) {
          close(candidateKind, { restoreFocus: false });
        }
      });
    }
    panel.classList.remove('hidden');
    panel.setAttribute('aria-hidden', 'false');
    bringToFront(panel);
    const openIndex = openOrder.indexOf(kind);
    if (openIndex >= 0) openOrder.splice(openIndex, 1);
    openOrder.push(kind);
    syncState();
    panel.focus?.();
  };

  triggers.forEach((trigger, kind) => {
    trigger.addEventListener('click', () => {
      const panel = panels.get(kind);
      if (panel && !panel.classList.contains('hidden')) {
        close(kind);
        return;
      }
      open(kind);
    });
  });
  layer
    .querySelectorAll('[data-action="close-research-report"]')
    .forEach((button) => {
      button.addEventListener('click', () => close(button.dataset.reportKind));
    });
  layer
    .querySelectorAll('[data-action="open-other-research-report"]')
    .forEach((button) => {
      button.addEventListener('click', () => open(button.dataset.reportKind));
    });
  panels.forEach((panel, kind) => {
    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      close(kind);
    });
  });
  syncState();

  return { open, close };
}
